import { format } from 'date-fns';
import { FileUp, Eye, Download, Trash2, Upload, AlertCircle, CheckCircle } from 'lucide-react';
import { API_URL } from '../../../services/api';
import { employeeService } from '../../../services/api';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { isFieldFilled } from '../../../lib/employeeHelpers';
import { canonicalizePanValue, isPanSentinelForUniqueness } from '../../../constants/panSentinels';
import AttachmentPreviewModal from '../../Common/AttachmentPreviewModal.jsx';
import { getAuthJson } from '../../../utils/authStorage';
import { isHeadOfOrganisationEmployee } from '../../../utils/headOfOrganisation';
import {
    isSameIndianMobile,
    isSameAadhaar,
    getEmployeeMobileNumber,
    EMERGENCY_PHONE_SAME_AS_EMPLOYEE,
    FAMILY_AADHAAR_SAME_AS_EMPLOYEE,
} from './validationHelpers';
import IssuedByCompanyPanel from './IssuedByCompanyPanel.jsx';

export function DocumentsTab({ employeeData, setEmployeeData, onSaveSection, loading, feedback, setIsUploadModalOpen, darkMode, onCancel, onNext, isLastTab, isEmployeeView = false, isPfApplicable, isEsiApplicable, totalEmployees = 0, headOfOrganisationEmployeeId = null, onFinalizeStateChange, onSaveTrigger, readOnly = false }) {
    const [docsSubTab, setDocsSubTab] = useState('submitted'); // submitted | issued
    const normalizeDocumentName = (value) => {
        const v = (value || '').trim();
        if (!v) return '';

        // Backward compatible aliases (old UI labels) -> canonical DB document_name
        const aliasMap = {
            'Pan & Aadhaar Linked Proof': 'PAN & Aadhaar Linked',
            'Proof of Bank Account': 'Bank Proof',
        };

        return aliasMap[v] || v;
    };

    /** One row per (family member, document type) for display/state; keeps newest id if DB has legacy duplicates. */
    const dedupeDocumentsByTypeAndFamily = (docs) => {
        const list = Array.isArray(docs) ? docs : [];
        const score = (d) => Number(d?.documentId ?? d?.id ?? 0);
        const byKey = new Map();
        for (const doc of list) {
            if (!doc) continue;
            const rawName = doc.documentName || doc.document_name || doc.type;
            const canonicalName = normalizeDocumentName(rawName);
            const fam = doc.familyId ?? doc.family_id ?? '';
            const key = `${fam}::${canonicalName}`;
            const prev = byKey.get(key);
            if (!prev || score(doc) >= score(prev)) {
                byKey.set(key, doc);
            }
        }
        return Array.from(byKey.values());
    };

    useEffect(() => {
        if (!employeeData?.id) return;
        let cancelled = false;

        (async () => {
            try {
                const resp = await employeeService.getEmployee(employeeData.id);
                const data = resp?.data || resp;
                const docs = data?.documents;
                if (!cancelled && Array.isArray(docs)) {
                    setEmployeeData(prev => ({
                        ...prev,
                        documents: docs,
                    }));
                }
            } catch (e) {
                // best-effort hydration; avoid blocking the tab
            }
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeData?.id]);

    // Ensure uploadedDocuments is always an array (dedupe so legacy duplicate rows do not appear twice)
    const allUploadedDocuments = dedupeDocumentsByTypeAndFamily(employeeData.documents || []);

    const shouldHideDocument = (doc) => {
        const docName = normalizeDocumentName(doc?.documentName || doc?.type);
        // Never show photos in Documents tab
        return docName === 'personal_images' || docName === 'family_images';
    };

    const getDisplayDocumentName = (doc) => {
        const docName = normalizeDocumentName(doc?.documentName || doc?.type);
        return docName;
    };

    const getFamilyMemberLabel = (doc) => {
        const familyId = doc?.familyId ?? doc?.family_id;
        if (!familyId) return '-';

        const members = employeeData?.familyMembers || [];
        const match = members.find(m => String(m?.id) === String(familyId));
        if (!match) return '-';

        const name = (
            match.name ||
            [match.firstName, match.lastName].filter(Boolean).join(' ').trim()
        );
        const relationship = match.relationship || match.relation;

        if (name) return name;
        if (relationship) return String(relationship);
        return '-';
    };

    // Filter out ESI document if ESI is not applicable
    const uploadedDocuments = allUploadedDocuments
        .map(doc => {
            if (!doc) return doc;
            // Backend may return snake_case keys; normalize once so the rest of the tab is consistent.
            const rawName = doc.documentName || doc.document_name || doc.type;
            const canonicalName = normalizeDocumentName(rawName);
            return {
                ...doc,
                documentName: canonicalName,
                familyId: doc.familyId ?? doc.family_id,
                filePath: doc.filePath || doc.file_path,
                originalFileName: doc.originalFileName || doc.original_file_name,
                downloadFileName: doc.downloadFileName || doc.download_file_name,
                lastUpdated: doc.lastUpdated || doc.last_updated,
            };
        })
        .filter(doc => {
        if (shouldHideDocument(doc)) return false;

        // Do not treat placeholders (metadata-only rows without any stored file) as uploaded documents.
        // This keeps the table and required-doc validation consistent.
        const hasFile = Boolean(
            doc && (
                doc.filePath ||
                doc.downloadFileName ||
                doc.originalFileName
            )
        );
        if (!hasFile) return false;

        const docName = normalizeDocumentName(doc?.documentName || doc?.type);
        if (docName === 'Form-2 (ESI)' && !isEsiApplicable) {
            return false; // Hide Form-2 (ESI) when ESI is not applicable
        }
        
        // ESI pehchan card: Only show to employee if admin added it (has file), but always show in admin view
        if (docName === 'ESI pehchan card') {
            // In employee view, only show if it exists (admin added it)
            // In admin view, always show if it exists
            return true; // If it has a file, it means admin added it, so show it
        }
        
        return true;
    });

    const [uploadStatus, setUploadStatus] = useState({});
    const [isUploading, setIsUploading] = useState(false);
    const [previewState, setPreviewState] = useState({
        isOpen: false,
        url: null,
        downloadUrl: null,
        fileName: null,
    });
    const [documentUrlCache, setDocumentUrlCache] = useState(() => new Map());
    const navigate = useNavigate();

    // Helper function to determine if a document is mandatory
    const isDocumentMandatory = (documentType) => {
        const mandatoryDocuments = [
            'Aadhaar',
            'PAN', 
            'PAN & Aadhaar Linked',
            'Bank Proof',
            'Form-F (POGA)'
        ];
        
        // Add PF form only if PF is applicable
        if (isPfApplicable && documentType === 'Form-11 (EPF)') {
            return true;
        }
        
        // Add ESI form only if ESI is applicable
        if (isEsiApplicable && documentType === 'Form-2 (ESI)') {
            return true;
        }

        // Educational certificates are only mandatory if user has more than primary education
        if (documentType === 'Educational Certificates') {
            return !employeeData?.lessThanPrimary;
        }

        // Professional certificates are only mandatory if user is not a fresher
        if (documentType === 'Professional Certificates') {
            return !employeeData?.isFresher;
        }

        return mandatoryDocuments.includes(documentType);
    };

    // Get all required document types based on employee status
    const getRequiredDocuments = () => {
        const requiredDocs = [
            'Aadhaar',
            'PAN', 
            'PAN & Aadhaar Linked',
            'Bank Proof',
            //'Form-11 (EPF)',
            'Form-F (POGA)',
            'Resume/CV'
        ];
        if (isPfApplicable) {
            requiredDocs.push('Form-11 (EPF)');
        }
        if (isEsiApplicable) {
            requiredDocs.push('Form-2 (ESI)');
        }

        // Add educational certificate only if user has more than primary education
        if (!employeeData?.lessThanPrimary) {
            requiredDocs.push('Educational Certificates');
        }

        // Add professional certificate only if user is not a fresher
        if (!employeeData?.isFresher) {
            requiredDocs.push('Professional Certificates');
        }

        return requiredDocs;
    };

    // Check if all required documents are uploaded
    const validateRequiredDocuments = () => {
        const requiredDocs = getRequiredDocuments();
        // A "uploaded" doc must exist AND have any stored file metadata (otherwise it's just a placeholder/metadata).
        const uploadedDocTypesWithFiles = (allUploadedDocuments || [])
            .map(doc => {
                if (!doc) return null;
                const rawName = doc.documentName || doc.document_name || doc.type;
                return {
                    ...doc,
                    documentName: normalizeDocumentName(rawName),
                    filePath: doc.filePath || doc.file_path,
                    originalFileName: doc.originalFileName || doc.original_file_name,
                    downloadFileName: doc.downloadFileName || doc.download_file_name,
                };
            })
            .filter(doc => {
                if (!doc) return false;
                const docName = normalizeDocumentName(doc.documentName || doc.type);
                if (docName === 'personal_images' || docName === 'family_images') return false;
                return Boolean(doc.filePath || doc.downloadFileName || doc.originalFileName);
            })
            .map(doc => normalizeDocumentName(doc.documentName || doc.type));
        
        const missingDocs = requiredDocs.filter(docType => 
            !uploadedDocTypesWithFiles.includes(docType)
        );

        return {
            isValid: missingDocs.length === 0,
            missingDocs
        };
    };

    // Cross-tab validation: only allow final save when all tabs have their required fields.
    // This intentionally mirrors each tab’s required-field rules (at a high level) to avoid enabling prematurely.
    const validateAllTabsRequiredFields = () => {
        const missingBySection = {};

        const addMissing = (section, message) => {
            if (!missingBySection[section]) missingBySection[section] = [];
            missingBySection[section].push(message);
        };

        // Helper validators
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
        const phoneRegex = /^[6-9]\d{9}$/;
        const aadhaarRegex = /^\d{12}$/;
        const pincodeRegex = /^\d{6}$/;
        const ifscRegex = /^[A-Za-z0-9]{11}$/;

        // Sentinel helpers (mirrors OrgDetailsForm / FamilyDetailsForm behavior)
        const isNAOrNotAvailable = (value) => {
            const v = (value || '').trim().toLowerCase();
            return v === 'na' || v === 'not available' || v === 'n/a';
        };

        const isPanNAOrNotAvailable = (value) => isPanSentinelForUniqueness(canonicalizePanValue(value));

        // --- Organization Details (mirrors OrgDetailsForm requirements) ---
        const orgSection = 'Organization Details';
        const empCode = employeeData.empCode;
        if (!isFieldFilled(empCode)) addMissing(orgSection, 'Employee Code is required');
        else if (!/^[a-zA-Z0-9\-/.]+$/.test(String(empCode))) {
            addMissing(orgSection, 'Employee Code can only contain letters, numbers, hyphens (-), forward slashes (/), and dots (.)');
        } else if (String(empCode).length > 12) {
            addMissing(orgSection, 'Employee Code cannot exceed 12 characters');
        }

        const userName = employeeData.user?.name;
        if (!isFieldFilled(userName)) addMissing(orgSection, 'Name is required');
        else if (!/^[a-zA-Z\s]+$/.test(String(userName))) addMissing(orgSection, 'Name can only contain letters and spaces');

        const dob = employeeData.user?.dateOfBirth;
        if (!isFieldFilled(dob)) addMissing(orgSection, 'Date of Birth is required');
        else {
            const birthDate = new Date(dob);
            if (isNaN(birthDate.getTime())) addMissing(orgSection, 'Date of Birth is invalid');
            else {
                const today = new Date();
                let age = today.getFullYear() - birthDate.getFullYear();
                const m = today.getMonth() - birthDate.getMonth();
                if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
                if (age < 18) addMissing(orgSection, 'Employee must be at least 18 years old');
                if (age > 100) addMissing(orgSection, 'Employee age cannot be more than 100 years');
            }
        }

        const gender = employeeData.user?.gender;
        if (!isFieldFilled(gender)) addMissing(orgSection, 'Gender is required');
        const bloodGroup = employeeData.user?.bloodGroup;
        if (!isFieldFilled(bloodGroup)) addMissing(orgSection, 'Blood Group is required');

        const userPhone = employeeData.user?.phone;
        if (!isFieldFilled(userPhone)) addMissing(orgSection, 'Mobile number is required');
        else if (!phoneRegex.test(String(userPhone))) addMissing(orgSection, 'Mobile number must be a valid 10-digit Indian number starting with 6/7/8/9');

        const personalEmail = employeeData.user?.email;
        if (!isFieldFilled(personalEmail) && !isNAOrNotAvailable(personalEmail)) {
            addMissing(orgSection, 'Personal Email is required');
        } else if (isFieldFilled(personalEmail) && !isNAOrNotAvailable(personalEmail) && !emailRegex.test(String(personalEmail))) {
            addMissing(orgSection, 'Personal Email format is invalid');
        }

        const officialEmail = employeeData.email || employeeData.officialEmail;
        if (!isFieldFilled(officialEmail) && !isNAOrNotAvailable(officialEmail)) {
            addMissing(orgSection, 'Official Email is required');
        } else if (isFieldFilled(officialEmail) && !isNAOrNotAvailable(officialEmail) && !emailRegex.test(String(officialEmail))) {
            addMissing(orgSection, 'Official Email format is invalid');
        }

        if (!isFieldFilled(employeeData.branchId)) addMissing(orgSection, 'Branch is required');
        if (!isFieldFilled(employeeData.designationId)) addMissing(orgSection, 'Designation is required');
        if (!isFieldFilled(employeeData.departmentId)) addMissing(orgSection, 'Department is required');
        if (!isFieldFilled(employeeData.subDepartmentId)) addMissing(orgSection, 'Sub Department is required');
        if (!isFieldFilled(employeeData.gradeId)) addMissing(orgSection, 'Grade is required');
        if (!isFieldFilled(employeeData.categoryId)) addMissing(orgSection, 'Category is required');
        if (!isFieldFilled(employeeData.employmentType)) addMissing(orgSection, 'Employment Type is required');
        if (!isFieldFilled(employeeData.employmentStatus)) addMissing(orgSection, 'Employment Status is required');

        const pan = employeeData.panNumber;
        if (!isFieldFilled(pan) && !isPanNAOrNotAvailable(pan)) {
            addMissing(orgSection, 'PAN Number is required');
        } else if (isFieldFilled(pan) && !isPanNAOrNotAvailable(pan) && !panRegex.test(String(pan).trim().toUpperCase())) {
            addMissing(orgSection, 'PAN Number format must be ABCDE1234F, or use PANNOTAVBL / PANAPPLIED');
        } else if (isFieldFilled(pan) && !isPanNAOrNotAvailable(pan)) {
            const panUpper = String(pan).trim().toUpperCase();
            if (panUpper.length === 10 && panUpper[3] !== 'P') {
                addMissing(orgSection, "PAN 4th character must be 'P' for individual employees (e.g. ABCPD1234F)");
            }
        }

        const aadhaar = employeeData.aadharNumber;
        if (!isFieldFilled(aadhaar)) addMissing(orgSection, 'Aadhaar Number is required');
        else if (!aadhaarRegex.test(String(aadhaar).trim())) addMissing(orgSection, 'Aadhaar Number must be exactly 12 digits');
        else if (['000000000000', '111111111111', '999999999999'].includes(String(aadhaar).trim())) addMissing(orgSection, 'Please enter a valid Aadhaar number');

        const ctc = employeeData.ctc;
        if (!isFieldFilled(ctc)) addMissing(orgSection, 'CTC is required');
        else if (isNaN(parseFloat(String(ctc))) || parseFloat(String(ctc)) <= 0) addMissing(orgSection, 'CTC must be a positive number');

        const joiningDate = employeeData.joiningDate;
        if (!isFieldFilled(joiningDate)) addMissing(orgSection, 'Joining Date is required');
        else {
            const jd = new Date(joiningDate);
            if (isNaN(jd.getTime())) addMissing(orgSection, 'Joining Date is invalid');
            else {
                const today = new Date();
                if (jd > today) addMissing(orgSection, 'Joining Date cannot be in the future');
                if (dob) {
                    const bd = new Date(dob);
                    if (!isNaN(bd.getTime())) {
                        let ageAtJoining = jd.getFullYear() - bd.getFullYear();
                        const md = jd.getMonth() - bd.getMonth();
                        if (md < 0 || (md === 0 && jd.getDate() < bd.getDate())) ageAtJoining--;
                        if (ageAtJoining < 18) addMissing(orgSection, 'Employee must be at least 18 years old at joining date');
                    }
                }
            }
        }

        // Reporting manager: required when org has employees, except Head of Organisation
        const userRole = employeeData.user?.role;
        const isAdmin = userRole === "admin" || userRole === "super_admin";
        const companyLike = {
          headOfOrganisationEmployeeId:
            headOfOrganisationEmployeeId ??
            getAuthJson("companyData", null)?.headOfOrganisationEmployeeId,
        };
        const isOrgHead = isHeadOfOrganisationEmployee(employeeData?.id, companyLike);
        if (
          !isOrgHead &&
          Number(totalEmployees) > 0 &&
          !isFieldFilled(employeeData.reportingManagerId) &&
          !isAdmin
        ) {
            addMissing(orgSection, 'Reporting Manager is required');
        }

        // Employment-status-specific required fields (mirrors OrgDetailsForm)
        const status = employeeData.employmentStatus;
        if (status === 'Confirmed') {
            if (!isFieldFilled(employeeData.confirmationDate)) addMissing(orgSection, 'Confirmation Date is required for confirmed employees');
        }
        if (status === 'Resigned' || status === 'Relieved') {
            if (!isFieldFilled(employeeData.resignationDate)) addMissing(orgSection, 'Resignation Date is required for resigned/relieved employees');
            if (!isFieldFilled(employeeData.relievedDate)) addMissing(orgSection, 'Relieved Date is required for resigned/relieved employees');
            if (!isFieldFilled(employeeData.reason)) addMissing(orgSection, 'Resignation Reason is required for resigned/relieved employees');
        }
        if (status === 'Terminated') {
            if (!isFieldFilled(employeeData.terminationDate)) addMissing(orgSection, 'Termination Date is required for terminated employees');
            if (!isFieldFilled(employeeData.reason)) addMissing(orgSection, 'Termination Reason is required for terminated employees');
        }

        // --- Personal Details (mirrors PersonalDetailsForm required fields) ---
        const personalSection = 'Personal Details';
        if (!isFieldFilled(employeeData.photo)) addMissing(personalSection, 'Employee photo is required');

        const addr = employeeData.address || {};
        if (!isFieldFilled(addr.address)) addMissing(personalSection, 'Present Address is required');
        if (!isFieldFilled(addr.city)) addMissing(personalSection, 'Present City is required');
        if (!isFieldFilled(addr.state)) addMissing(personalSection, 'Present State is required');
        if (!isFieldFilled(addr.pincode)) addMissing(personalSection, 'Present Pincode is required');
        else if (!pincodeRegex.test(String(addr.pincode).trim())) addMissing(personalSection, 'Present Pincode must be 6 digits');

        if (!isFieldFilled(addr.permanentAddress)) addMissing(personalSection, 'Permanent Address is required');
        if (!isFieldFilled(addr.permanentCity)) addMissing(personalSection, 'Permanent City is required');
        if (!isFieldFilled(addr.permanentState)) addMissing(personalSection, 'Permanent State is required');
        if (!isFieldFilled(addr.permanentPincode)) addMissing(personalSection, 'Permanent Pincode is required');
        else if (!pincodeRegex.test(String(addr.permanentPincode).trim())) addMissing(personalSection, 'Permanent Pincode must be 6 digits');

        if (!isFieldFilled(employeeData.emergencyContactName)) addMissing(personalSection, 'Emergency Contact Name is required');
        if (!isFieldFilled(employeeData.emergencyContactPhone)) addMissing(personalSection, 'Emergency Contact Phone is required');
        else if (!phoneRegex.test(String(employeeData.emergencyContactPhone))) addMissing(personalSection, 'Emergency Contact Phone must be a valid 10-digit number starting with 6/7/8/9');
        else if (
            isSameIndianMobile(
                employeeData.emergencyContactPhone,
                getEmployeeMobileNumber(employeeData)
            )
        ) {
            addMissing(personalSection, EMERGENCY_PHONE_SAME_AS_EMPLOYEE);
        }
        if (!isFieldFilled(employeeData.emergencyContactRelation)) addMissing(personalSection, 'Emergency Contact Relationship is required');

        if (!isFieldFilled(employeeData.maritalStatus)) addMissing(personalSection, 'Marital Status is required');
        if (!isFieldFilled(employeeData.fatherName)) addMissing(personalSection, 'Father Name is required');

        // --- Family Details (mirrors FamilyDetailsForm) ---
        const familySection = 'Family Details';
        const isESIEnabled = employeeData?.esiApplicable === true;
        if (!employeeData.isOrphan) {
            const members = Array.isArray(employeeData.familyMembers) ? employeeData.familyMembers : [];
            if (members.length === 0) {
                addMissing(familySection, 'Please either mark as orphan or add at least one family member');
            } else {
                // Validate each family member with the same core rules as FamilyDetailsForm
                const nomineeShares = [];
                members.forEach((m, idx) => {
                    const prefix = `Family member #${idx + 1}`;
                    const name = m?.name;
                    const dateOfBirth = m?.dateOfBirth || m?.date_of_birth;
                    const relationship = m?.relationship;
                    const gender = m?.gender;
                    const phone = m?.phone || m?.mobile_number || m?.mobileNumber;
                    const aadhaar = m?.aadhaarNumber || m?.aadhaar_number || m?.aadhaar;

                    if (!isFieldFilled(name)) addMissing(familySection, `${prefix}: Name is required`);
                    if (!isFieldFilled(dateOfBirth)) addMissing(familySection, `${prefix}: Date of birth is required`);
                    if (!isFieldFilled(relationship)) addMissing(familySection, `${prefix}: Relationship is required`);
                    if (!isFieldFilled(gender)) addMissing(familySection, `${prefix}: Gender is required`);

                    if (!isFieldFilled(phone) && !isNAOrNotAvailable(phone)) {
                        addMissing(familySection, `${prefix}: Mobile number is required`);
                    } else if (isFieldFilled(phone) && !isNAOrNotAvailable(phone) && !phoneRegex.test(String(phone))) {
                        addMissing(familySection, `${prefix}: Mobile number must be a valid 10-digit Indian number starting with 6/7/8/9`);
                    }

                    if (!isFieldFilled(aadhaar) && !isNAOrNotAvailable(aadhaar)) {
                        addMissing(familySection, `${prefix}: Aadhaar number is required`);
                    } else if (isFieldFilled(aadhaar) && !isNAOrNotAvailable(aadhaar) && !aadhaarRegex.test(String(aadhaar))) {
                        addMissing(familySection, `${prefix}: Aadhaar number must be 12 digits`);
                    } else if (isFieldFilled(aadhaar) && !isNAOrNotAvailable(aadhaar) && ['000000000000', '111111111111', '999999999999'].includes(String(aadhaar))) {
                        addMissing(familySection, `${prefix}: Please enter a valid Aadhaar number`);
                    } else if (isSameAadhaar(aadhaar, employeeData.aadharNumber)) {
                        addMissing(familySection, `${prefix}: ${FAMILY_AADHAAR_SAME_AS_EMPLOYEE}`);
                    }

                    const nominee = Boolean(m?.nominee);
                    const sharePercentage = m?.sharePercentage ?? m?.share_percentage;
                    const photo = m?.photo || m?.photoFileName || m?.photo_file_name;

                    if (nominee) {
                        const share = parseFloat(String(sharePercentage ?? ''));
                        if (!isFieldFilled(sharePercentage)) addMissing(familySection, `${prefix}: Please enter share percentage for nominee`);
                        else if (isNaN(share) || share <= 0) addMissing(familySection, `${prefix}: Share percentage must be greater than 0%`);
                        else if (share > 100) addMissing(familySection, `${prefix}: Share percentage cannot exceed 100%`);
                        else nomineeShares.push(share);
                    }

                    if (isESIEnabled && !isFieldFilled(photo)) {
                        addMissing(familySection, `${prefix}: Photo is mandatory for ESI filing`);
                    }
                });

                const totalNomineeShare = nomineeShares.reduce((t, s) => t + s, 0);
                if (totalNomineeShare > 100) {
                    addMissing(familySection, `Total nominee share percentage cannot exceed 100% (current total: ${totalNomineeShare.toFixed(2)}%)`);
                }
            }
        }

        // --- Bank Details (mirrors BankDetailsForm) ---
        const bankSection = 'Bank Details';
        if (!isFieldFilled(employeeData.bankName)) addMissing(bankSection, 'Bank Name is required');
        if (!isFieldFilled(employeeData.accountNumber)) addMissing(bankSection, 'Account Number is required');
        if (!isFieldFilled(employeeData.ifscCode)) addMissing(bankSection, 'IFSC Code is required');
        else if (!ifscRegex.test(String(employeeData.ifscCode).trim())) addMissing(bankSection, 'IFSC Code must be exactly 11 letters/numbers');
        if (!isFieldFilled(employeeData.bankBranch)) addMissing(bankSection, 'Bank Branch is required');

        // --- Educational Details (mirrors QualificationDetailsForm) ---
        const eduSection = 'Educational Details';
        if (!employeeData.lessThanPrimary) {
            const quals = Array.isArray(employeeData.qualifications) ? employeeData.qualifications : [];
            if (quals.length === 0) {
                addMissing(eduSection, 'Please either mark as less than primary education or add at least one qualification');
            } else {
                quals.forEach((q, idx) => {
                    const prefix = `Qualification #${idx + 1}`;
                    if (!isFieldFilled(q?.degree)) addMissing(eduSection, `${prefix}: Degree is required`);
                    if (!isFieldFilled(q?.yearOfPassing)) addMissing(eduSection, `${prefix}: Year of passing is required`);
                    else if (!/^\d{4}$/.test(String(q.yearOfPassing))) addMissing(eduSection, `${prefix}: Year must be exactly 4 digits (e.g., 2023)`);
                    if (!isFieldFilled(q?.institution)) addMissing(eduSection, `${prefix}: Institution is required`);
                    if (!isFieldFilled(q?.percentage)) addMissing(eduSection, `${prefix}: Percentage/Grade/Class is required`);
                    if (!isFieldFilled(q?.specialization)) addMissing(eduSection, `${prefix}: Specialization is required`);
                });
            }
        }

        // --- Professional Details (mirrors ProfessionalDetailsForm) ---
        const profSection = 'Professional Details';
        if (!employeeData.isFresher) {
            const exps = Array.isArray(employeeData.experiences) ? employeeData.experiences : [];
            if (exps.length === 0) {
                addMissing(profSection, 'Please either mark as fresher or add at least one experience');
            } else {
                exps.forEach((e, idx) => {
                    const prefix = `Experience #${idx + 1}`;
                    if (!isFieldFilled(e?.company)) addMissing(profSection, `${prefix}: Company name is required`);
                    if (!isFieldFilled(e?.position)) addMissing(profSection, `${prefix}: Position is required`);
                    if (!isFieldFilled(e?.fromDate)) addMissing(profSection, `${prefix}: From date is required`);
                    if (!isFieldFilled(e?.toDate)) addMissing(profSection, `${prefix}: To date is required`);
                    if (e?.fromDate && e?.toDate) {
                        const fd = new Date(e.fromDate);
                        const td = new Date(e.toDate);
                        if (!isNaN(fd.getTime()) && !isNaN(td.getTime()) && td < fd) {
                            addMissing(profSection, `${prefix}: To Date cannot be earlier than From Date`);
                        }
                    }
                });
            }
        }

        // --- Statutory Details (mirrors StatutoryDetailsForm behavior) ---
        const statutorySection = 'Statutory Details';
        const pfApplicable = employeeData?.pfApplicable === true;
        const esiApplicable = employeeData?.esiApplicable === true;
        const isOnlyEsiApplicable = esiApplicable && !pfApplicable;

        if (!isOnlyEsiApplicable) {
            // Their form enforces: hasUAN must be selected and must be YES
            if (employeeData.hasUAN === null || employeeData.hasUAN === undefined) {
                addMissing(statutorySection, 'Please select whether you have a UAN number');
            } else if (employeeData.hasUAN === false) {
                addMissing(statutorySection, 'UAN number is mandatory to proceed. You must have a UAN number.');
            } else {
                if (!isFieldFilled(employeeData.pfUANNumber)) addMissing(statutorySection, 'PF UAN Number is required');
                else if (!/^\d{12}$/.test(String(employeeData.pfUANNumber))) addMissing(statutorySection, 'PF UAN Number must be exactly 12 digits');
            }

            if (employeeData.epsEligiblePreviousCompany === null || employeeData.epsEligiblePreviousCompany === undefined) {
                addMissing(statutorySection, 'Please select whether you are eligible for EPS deduction in your previous company');
            }
        }

        if (esiApplicable) {
            if (employeeData.hasESIIP === null || employeeData.hasESIIP === undefined) {
                addMissing(statutorySection, 'Please select whether you hold an ESI IP number');
            }
            if (!isFieldFilled(employeeData.esiState)) addMissing(statutorySection, 'ESI State is required');
            if (!isFieldFilled(employeeData.esiDistrict)) addMissing(statutorySection, 'ESI District is required');
            if (!isFieldFilled(employeeData.esiDispensary)) addMissing(statutorySection, 'ESI Dispensary is required');
            else if (!/^[A-Za-z\s]+$/.test(String(employeeData.esiDispensary))) addMissing(statutorySection, 'ESI Dispensary must contain only alphabets and spaces');

            if (employeeData.hasESIIP === true) {
                if (!isFieldFilled(employeeData.esiIPNumber)) addMissing(statutorySection, 'ESI IP Number is required');
                else if (!/^\d{10}$/.test(String(employeeData.esiIPNumber))) addMissing(statutorySection, 'ESI IP Number must be exactly 10 digits');
            }
        }

        const isValid = Object.keys(missingBySection).length === 0;
        return { isValid, missingBySection };
    };

    const showValidationToasts = ({ tabsValidation, docsValidation }) => {
        // Prefer a compact summary toast + a small number of detail toasts (avoid spamming).
        if (tabsValidation && !tabsValidation.isValid) {
            const sections = Object.keys(tabsValidation.missingBySection);
            toast.error(`Please complete required fields in: ${sections.join(', ')}`, {
                position: 'top-right',
                duration: 5000,
            });
            // Show up to 2 example missing items to guide the user quickly.
            const firstSection = sections[0];
            const firstItems = tabsValidation.missingBySection[firstSection] || [];
            if (firstItems.length > 0) {
                toast.error(firstItems.slice(0, 2).join(' • '), {
                    position: 'top-right',
                    duration: 5000,
                });
            }
        }

        if (docsValidation && !docsValidation.isValid) {
            toast.error(`Please upload required documents: ${docsValidation.missingDocs.slice(0, 6).join(', ')}${docsValidation.missingDocs.length > 6 ? '...' : ''}`, {
                position: 'top-right',
                duration: 6000,
            });
        }
    };

    const handleSave = async () => {
        if (readOnly) {
            toast.error('Click Edit to make changes before saving.', { position: 'top-right' });
            return { saved: false };
        }
        const tabsValidation = validateAllTabsRequiredFields();
        const docsValidation = validateRequiredDocuments();

        if (!tabsValidation.isValid || !docsValidation.isValid) {
            showValidationToasts({ tabsValidation, docsValidation });
            return { saved: false };
        }

        // Only send documents section fields
        const sectionData = {
            documents: (uploadedDocuments || []).map(doc => ({
                documentName: normalizeDocumentName(doc.documentName || doc.type || ''),
                fileName: doc.fileName || doc.documentNumber || '',
                size: doc.size || 0,
                lastUpdated: doc.lastUpdated || doc.issueDate || new Date().toISOString(),
                comment: doc.comment || doc.description || '',
            })),
        };
        
        try {
            await onSaveSection(sectionData);
            
            // For employee view, navigate to employee home page with success toast
            if (isEmployeeView) {
                toast.success('Employee details updated successfully!', {
                    position: 'top-right',
                    duration: 4000,
                });
                navigate('/employee/home');
                return { saved: true };
            } else {
                // For admin view, show the same success toast.
                toast.success('Employee details updated successfully!', {
                    position: 'top-right',
                    duration: 4000,
                });

                // If this is the last tab (or we don't have next), close.
                if (isLastTab || !onNext) {
                    if (onCancel) onCancel();
                    return { saved: true };
                }

                // Otherwise move to next tab.
                if (onNext) onNext();
                return { saved: true };
            }
        } catch (error) {
            // If save fails, stay on current tab
            console.error('Save failed:', error);
            const msg =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                'Failed to save. Please try again.';
            toast.error(msg, { position: 'top-right' });
            return { saved: false };
        }
    };

    const handleViewDocument = async (document) => {
        if (!document) return;
        const documentId = document.documentId || document.id;
        if (!documentId) return;

        try {
            let cached = documentUrlCache.get(documentId);
            if (!cached) {
                const [previewResp, downloadResp] = await Promise.all([
                    employeeService.getEmployeeDocumentUrl(documentId),
                    employeeService.getEmployeeDocumentDownloadUrl(documentId),
                ]);
                const url = previewResp?.data?.url || previewResp?.url || null;
                const downloadUrl = downloadResp?.data?.url || downloadResp?.url || null;
                cached = { url, downloadUrl };
                setDocumentUrlCache(prev => {
                    const next = new Map(prev);
                    next.set(documentId, cached);
                    return next;
                });
            }

            const url = cached?.url;
            const downloadUrl = cached?.downloadUrl;
            if (!url) throw new Error('Missing preview url');

            const fileName =
                document.downloadFileName ||
                document.originalFileName ||
                document.fileName ||
                document.documentName ||
                'Document';

            setPreviewState({
                isOpen: true,
                url,
                downloadUrl: downloadUrl || url,
                fileName,
            });
        } catch (e) {
            console.error('Failed to open document', e);
            toast.error('Failed to open document');
        }
    };

    const handleDownloadDocument = async (doc) => {
        if (!doc) return;
        const documentId = doc.documentId || doc.id;
        if (!documentId) return;

        try {
            const resp = await employeeService.getEmployeeDocumentDownloadUrl(documentId);
            const url = resp?.data?.url || resp?.url || null;
            if (!url) throw new Error('No download url');

            const fileName = doc.downloadFileName || doc.originalFileName || doc.fileName || 'document';
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading document:', error);
            toast.error('Failed to download document');
        }
    };

    const handleDeleteDocument = (index) => {
        if (window.confirm('Are you sure you want to delete this document?')) {
            const updatedDocuments = uploadedDocuments.filter((_, i) => i !== index);
            setEmployeeData(prev => ({ ...prev, documents: updatedDocuments }));
        }
    };

    const handleFileUpload = async (event, documentIndex) => {
        const file = event.target.files[0];
        if (!file) return;

        if (isUploading) {
            toast.error('Please wait for the current upload to finish.', { position: 'top-right' });
            event.target.value = '';
            return;
        }

        // Validate file size (5MB limit)
        if (file.size > 5 * 1024 * 1024) {
            setUploadStatus(prev => ({
                ...prev,
                [documentIndex]: { type: 'error', message: 'File size must be less than 5MB' }
            }));
            return;
        }

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (!allowedTypes.includes(file.type)) {
            setUploadStatus(prev => ({
                ...prev,
                [documentIndex]: { type: 'error', message: 'Only PDF, Word, and image files are allowed' }
            }));
            return;
        }

        setIsUploading(true);
        setUploadStatus(prev => ({
            ...prev,
            [documentIndex]: { type: 'info', message: 'Uploading...' }
        }));

        try {
            const formData = new FormData();
            formData.append('document', file);
            formData.append('documentName', normalizeDocumentName(uploadedDocuments[documentIndex]?.documentName || uploadedDocuments[documentIndex]?.type) || 'Document');
            formData.append('employeeId', employeeData.id);
            formData.append('replaceExisting', 'true');

            const response = await employeeService.uploadDocument(formData);

            console.log("response");  
            console.log(response);  
            if (response) {
                const result = response;
                
                // Update the document with new file information
                const updatedDocuments = [...uploadedDocuments];
                updatedDocuments[documentIndex] = {
                    ...updatedDocuments[documentIndex],
                    documentId: result.documentId || updatedDocuments[documentIndex]?.documentId,
                    filePath: result.filePath,
                    originalFileName: result.originalFileName,
                    downloadFileName: result.downloadFileName,
                    fileName: result.downloadFileName || result.originalFileName,
                    size: file.size,
                    lastUpdated: new Date().toISOString(),
                };

                setEmployeeData(prev => ({ ...prev, documents: updatedDocuments }));
                
                setUploadStatus(prev => ({
                    ...prev,
                    [documentIndex]: { type: 'success', message: 'File uploaded successfully!' }
                }));

                // Clear success message after 3 seconds
                setTimeout(() => {
                    setUploadStatus(prev => {
                        const newStatus = { ...prev };
                        delete newStatus[documentIndex];
                        return newStatus;
                    });
                }, 3000);
            } else {
                throw new Error('Upload failed');
            }
        } catch (error) {
            console.log(error);
            setUploadStatus(prev => ({
                ...prev,
                [documentIndex]: { type: 'error', message: 'Upload failed. Please try again.' }
            }));
        } finally {
            setIsUploading(false);
            // Clear file input
            event.target.value = '';
        }
    };

    const getFileIcon = (fileName) => {
        if (!fileName) return '📄';
        const extension = fileName.split('.').pop()?.toLowerCase();
        if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)) {
            return '🖼️';
        } else if (extension === 'pdf') {
            return '📄';
        } else {
            return '📎';
        }
    };

    const getStatusIcon = (type) => {
        switch (type) {
            case 'success':
                return <CheckCircle className="w-4 h-4 text-emerald-600" />;
            case 'error':
                return <AlertCircle className="w-4 h-4 text-red-500" />;
            case 'info':
                return <div className="w-4 h-4 border-2 border-[hsl(var(--ds-primary))] border-t-transparent rounded-full animate-spin"></div>;
            default:
                return null;
        }
    };

    const getStatusColor = (type) => {
        switch (type) {
            case 'success':
                return 'border-emerald-200 bg-emerald-50 text-emerald-800';
            case 'error':
                return 'text-red-600 bg-red-50 border-red-200';
            case 'info':
                return 'text-[hsl(var(--ds-secondary))] bg-[hsl(var(--ds-accent))]/40 border-[hsl(var(--ds-border))]';
            default:
                return '';
        }
    };

    const tabsValidation = validateAllTabsRequiredFields();
    const docsValidation = validateRequiredDocuments();
    const canFinalize = !loading && !isUploading && tabsValidation.isValid && docsValidation.isValid;

    // Inform parent (footer) whether we can finalize from Documents tab.
    useEffect(() => {
        if (typeof onFinalizeStateChange === 'function') {
            onFinalizeStateChange({
                canFinalize,
                tabsValid: tabsValidation.isValid,
                docsValid: docsValidation.isValid,
                missingTabsCount: Object.keys(tabsValidation.missingBySection || {}).length,
                missingDocsCount: (docsValidation.missingDocs || []).length,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canFinalize, tabsValidation.isValid, docsValidation.isValid, isUploading, loading, employeeData?.id]);

    // Expose a save attempt to the parent so footer button can show toasts and never bypass validation.
    useEffect(() => {
        if (typeof onSaveTrigger === 'function') {
            onSaveTrigger(() => handleAttemptFinalize());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSaveTrigger, canFinalize, tabsValidation.isValid, docsValidation.isValid, employeeData?.id]);

    const handleAttemptFinalize = () => {
        if (readOnly) {
            toast.error('Click Edit to make changes before saving.', { position: 'top-right' });
            return { saved: false };
        }
        const latestTabs = validateAllTabsRequiredFields();
        const latestDocs = validateRequiredDocuments();
        if (!latestTabs.isValid || !latestDocs.isValid) {
            showValidationToasts({ tabsValidation: latestTabs, docsValidation: latestDocs });
            return { saved: false };
        }
        return handleSave();
    };

    return (
        <div className="space-y-4">
            <div className="flex gap-1 border-b border-[hsl(var(--ds-border))]">
                <button
                    type="button"
                    onClick={() => setDocsSubTab('submitted')}
                    className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                        docsSubTab === 'submitted'
                            ? 'border-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary))]'
                            : 'border-transparent text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]'
                    }`}
                >
                    Submitted By Employee
                </button>
                <button
                    type="button"
                    onClick={() => setDocsSubTab('issued')}
                    className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                        docsSubTab === 'issued'
                            ? 'border-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary))]'
                            : 'border-transparent text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]'
                    }`}
                >
                    Issued by Company
                </button>
            </div>

            {docsSubTab === 'issued' ? (
                <IssuedByCompanyPanel
                    employeeId={employeeData?.id}
                    isEmployeeView={isEmployeeView}
                    readOnly={readOnly}
                />
            ) : (
            <>
            <div className="flex justify-between items-center">
                <h3 className="font-semibold text-lg">Proof Documents</h3>
                {(() => {
                    // Get all available document options (same logic as ProofDocumentModal)
                    const getAllAvailableDocuments = () => {
                        const baseOptions = [
                            { value: 'Resume/CV', mandatory: true },
                            { value: 'Aadhaar', mandatory: true },
                            { value: 'PAN', mandatory: true },
                            { value: 'PAN & Aadhaar Linked', mandatory: true },
                            { value: 'Bank Proof', mandatory: true },
                            { value: 'Form-11 (EPF)', mandatory: true, requiresPf: true },
                            { value: 'Form-2 (ESI)', mandatory: true, requiresEsi: true },
                            { value: 'Form-F (POGA)', mandatory: true },
                            { value: 'ESI pehchan card', mandatory: false, requiresEsi: true, adminOnly: true }
                        ];

                        if (!employeeData?.lessThanPrimary) {
                            baseOptions.push({ value: 'Educational Certificates', mandatory: true });
                        }

                        if (!employeeData?.isFresher) {
                            baseOptions.push({ value: 'Professional Certificates', mandatory: true });
                        }

                        // Check uploaded documents from backend - must have file metadata
                        const uploadedDocTypes = (allUploadedDocuments || [])
                            .filter(doc => {
                                if (!doc) return false;
                                // Only count documents that have actual file metadata (not placeholders)
                                return Boolean(
                                    doc.filePath || 
                                    doc.file_path || 
                                    doc.downloadFileName || 
                                    doc.download_file_name || 
                                    doc.originalFileName || 
                                    doc.original_file_name
                                );
                            })
                            .map(doc => {
                                const rawName = doc.documentName || doc.document_name || doc.type;
                                return normalizeDocumentName(rawName);
                            })
                            .filter(Boolean);

                        const esiApplicable = isEsiApplicable === true || 
                                             isEsiApplicable === 1 ||
                                             employeeData?.esiApplicable === true ||
                                             employeeData?.esiApplicable === 1 ||
                                             employeeData?.persistedEsiApplicable === true ||
                                             employeeData?.persistedEsiApplicable === 1;

                        return baseOptions
                            .filter(option => {
                                if (option.requiresPf) {
                                    return isPfApplicable === true || isPfApplicable === 1 || employeeData?.pfApplicable === true || employeeData?.pfApplicable === 1;
                                }
                                if (option.requiresEsi) {
                                    return esiApplicable;
                                }
                                return true;
                            })
                            .filter(option => {
                                // Check if document is already uploaded
                                const isUploaded = uploadedDocTypes.includes(normalizeDocumentName(option.value));
                                
                                // Admin-only optional documents: show if not uploaded (for admin to upload), hide if uploaded
                                if (option.adminOnly && !option.mandatory && !isEmployeeView) {
                                    return !isUploaded; // Hide if already uploaded
                                }
                                
                                // For other documents, filter out if already uploaded
                                return !isUploaded;
                            });
                    };

                    const availableDocs = getAllAvailableDocuments();
                    const remainingCount = availableDocs.length;

                    if (remainingCount > 0) {
                        return (
                            <button
                                onClick={() => !readOnly && setIsUploadModalOpen(true)}
                                className={`px-3 py-1.5 text-sm font-medium rounded flex items-center transition-colors ${
                                    readOnly 
                                        ? 'opacity-60 cursor-not-allowed bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]' 
                                        : 'inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3.5 py-2 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 hover:opacity-95 hover:text-white'
                                }`}
                                disabled={readOnly}
                            >
                                <FileUp className="mr-1.5" size={16} />
                                Add Files Here ({remainingCount} remaining)
                            </button>
                        );
                    }
                    return null; // Hide button when all documents are uploaded
                })()}
            </div>

            {/* Missing required fields summary (other tabs) */}
            {!tabsValidation.isValid && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center text-red-700">
                        <AlertCircle className="w-4 h-4 mr-2" />
                        <span className="text-sm font-medium">Incomplete Required Fields (Previous Tabs):</span>
                    </div>
                    <div className="text-xs text-red-600 mt-1 space-y-1">
                        {Object.entries(tabsValidation.missingBySection).slice(0, 4).map(([section, items]) => (
                            <div key={section}>
                                <span className="font-semibold">{section}:</span>{' '}
                                <span>{(items || []).slice(0, 3).join(', ')}{(items || []).length > 3 ? ', ...' : ''}</span>
                            </div>
                        ))}
                        {Object.keys(tabsValidation.missingBySection).length > 4 && (
                            <div>...and more sections</div>
                        )}
                    </div>
                </div>
            )}

            {/* Document Requirements Summary */}
            {(() => {
                const validation = validateRequiredDocuments();
                if (!validation.isValid) {
                    return (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-center text-red-700">
                                <AlertCircle className="w-4 h-4 mr-2" />
                                <span className="text-sm font-medium">Missing Required Documents:</span>
                            </div>
                            <p className="text-xs text-red-600 mt-1">
                                {validation.missingDocs.join(', ')}
                            </p>
                        </div>
                    );
                }
                return (
                    <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                        <div className="flex items-center text-emerald-800">
                            <CheckCircle className="w-4 h-4 mr-2" />
                            <span className="text-sm font-medium">All Required Documents Uploaded ✅</span>
                        </div>
                    </div>
                );
            })()}

            <div className="border rounded-lg overflow-hidden" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table className="w-full">
                    <thead className="bg-[hsl(var(--ds-muted))] sticky top-0 z-20">
                        <tr>
                            <th className="px-4 py-2 text-left text-sm font-medium text-[hsl(var(--ds-muted-fg))]">Sr. No.</th>
                            <th className="px-4 py-2 text-left text-sm font-medium text-[hsl(var(--ds-muted-fg))]">Document Type</th>
                            <th className="px-4 py-2 text-left text-sm font-medium text-[hsl(var(--ds-muted-fg))]">Upload Date</th>
                            <th className="px-4 py-2 text-left text-sm font-medium text-[hsl(var(--ds-muted-fg))]">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {uploadedDocuments.map((doc, index) => (
                            <tr key={doc?.documentId || doc?.id || `${doc?.documentName || doc?.type}-${index}`} className="border-t">
                                <td className="px-4 py-2 text-sm text-[hsl(var(--ds-muted-fg))]">{index + 1}</td>
                                <td className="px-4 py-2 text-sm">
                                    <div className="flex items-center">
                                        <span className="mr-2">{getFileIcon(doc.fileName || doc.downloadFileName || doc.originalFileName)}</span>
                                        {getDisplayDocumentName(doc)}
                                    </div>
                                </td>
                                <td className="px-4 py-2 text-sm text-[hsl(var(--ds-muted-fg))]">
                                    {doc.lastUpdated ? format(new Date(doc.lastUpdated), 'dd/MM/yyyy') : '-'}
                                </td>
                                <td className="px-4 py-2 text-sm">
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => handleViewDocument(doc)}
                                            className="p-1 text-[hsl(var(--ds-secondary))] hover:text-[hsl(var(--ds-secondary))]-dark hover:bg-[hsl(var(--ds-accent))]/40 rounded"
                                            title="View Document"
                                            disabled={isUploading}
                                        >
                                            <Eye size={16} />
                                        </button>
                                        <label className={`p-1 text-purple-600 rounded ${readOnly ? 'opacity-60 cursor-not-allowed pointer-events-none' : 'hover:text-purple-800 hover:bg-purple-50 cursor-pointer'}`} title="Replace Document">
                                            <Upload size={16} />
                                            <input
                                                type="file"
                                                className="hidden"
                                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                                                onChange={(e) => handleFileUpload(e, index)}
                                                disabled={isUploading || readOnly}
                                            />
                                        </label>
                                        <button
                                            onClick={() => handleDownloadDocument(doc)}
                                            className="rounded p-1 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-900"
                                            title="Download Document"
                                            disabled={isUploading}
                                        >
                                            <Download size={16} />
                                        </button>
                                        {/* <button
                                            onClick={() => handleDeleteDocument(index)}
                                            className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                                            title="Delete Document"
                                            disabled={isUploading}
                                        >
                                            <Trash2 size={16} />
                                        </button>  */}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {uploadedDocuments.length === 0 && (
                            <tr>
                                <td colSpan={5} className="px-4 py-8 text-center text-sm text-[hsl(var(--ds-muted-fg))]">
                                    No documents uploaded yet
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Status Messages */}
            {Object.keys(uploadStatus).length > 0 && (
                <div className="space-y-2">
                    {Object.entries(uploadStatus).map(([index, status]) => (
                        <div
                            key={index}
                            className={`flex items-center p-3 rounded-lg border ${getStatusColor(status.type)}`}
                        >
                            {getStatusIcon(status.type)}
                            <span className="ml-2 text-sm font-medium">{status.message}</span>
                        </div>
                    ))}
                </div>
            )}

            {feedback && (
                <div className={`mt-2 ${feedback.type === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>{feedback.message}</div>
            )}

            <AttachmentPreviewModal
                isOpen={previewState.isOpen}
                url={previewState.url}
                downloadUrl={previewState.downloadUrl}
                fileName={previewState.fileName}
                onClose={() =>
                    setPreviewState({
                        isOpen: false,
                        url: null,
                        downloadUrl: null,
                        fileName: null,
                    })
                }
            />
            </>
            )}
        </div>
    );
}